/**
 * Cleo ERP SaaS — Liste des factures billing admin
 */
import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Typography, Input, Select, Space, Alert, Button } from 'antd';
import { SearchOutlined, LinkOutlined } from '@ant-design/icons';
import api from '../../../api/axios';

const { Title, Text } = Typography;

const STATUS_COLORS = {
  PAID: '#10B981', BILLED: '#3B82F6', PAST_DUE: '#EF4444',
  CANCELLED: '#64748B', REFUNDED: '#F59E0B', DRAFT: '#94A3B8',
};
const STATUS_LABELS = {
  PAID: 'Payée', BILLED: 'Émise', PAST_DUE: 'En retard',
  CANCELLED: 'Annulée', REFUNDED: 'Remboursée', DRAFT: 'Brouillon',
};

function BillingInvoiceListPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: '', tenant: '' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.tenant) params.tenant = filters.tenant;
      const res = await api.get('/api/admin/billing/invoices/', { params });
      setInvoices(res.data);
    } catch {
      setError('Impossible de charger les factures.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filters]);

  const columns = [
    {
      title: 'Numéro',
      dataIndex: 'paddle_invoice_number',
      key: 'paddle_invoice_number',
      render: (num) => <Text strong style={{ fontFamily: 'monospace' }}>{num || '—'}</Text>,
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_slug',
      key: 'tenant_slug',
      render: (slug) => <Text strong>{slug}</Text>,
    },
    {
      title: 'Période',
      key: 'period',
      render: (_, r) => (
        <Text style={{ fontSize: 12 }}>
          {new Date(r.period_start).toLocaleDateString('fr-FR')} →{' '}
          {new Date(r.period_end).toLocaleDateString('fr-FR')}
        </Text>
      ),
    },
    {
      title: 'Montant TTC',
      key: 'amount_total',
      render: (_, r) => (
        <Text strong style={{ color: '#10B981' }}>
          {parseFloat(r.amount_total).toLocaleString('fr-FR')} {r.currency}
        </Text>
      ),
    },
    {
      title: 'Émise le',
      dataIndex: 'issued_at',
      key: 'issued_at',
      render: (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—',
    },
    {
      title: 'Échéance',
      dataIndex: 'due_at',
      key: 'due_at',
      render: (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—',
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (s) => (
        <Tag color={STATUS_COLORS[s] || '#CBD5E1'} style={{ borderRadius: 6 }}>
          {STATUS_LABELS[s] || s}
        </Tag>
      ),
    },
    {
      title: 'Pushée tenant',
      dataIndex: 'pushed_to_tenant_at',
      key: 'pushed_to_tenant_at',
      render: (d) => d
        ? <Tag color="#10B981" style={{ borderRadius: 6 }}>Oui</Tag>
        : <Tag color="#F59E0B" style={{ borderRadius: 6 }}>Non</Tag>,
    },
    {
      title: 'PDF',
      dataIndex: 'paddle_invoice_url',
      key: 'paddle_invoice_url',
      render: (url) => url
        ? <Button type="link" icon={<LinkOutlined />} href={url} target="_blank" size="small">PDF</Button>
        : <Text style={{ color: '#94A3B8' }}>—</Text>,
    },
  ];

  if (error) return <Alert message={error} type="error" showIcon />;

  return (
    <div>
      <Title level={4} style={{ color: '#0F172A', marginBottom: 16 }}>Factures</Title>

      <Card size="small" style={{ borderRadius: 12, border: '1px solid #E5E7EB', marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Rechercher tenant..."
            prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
            style={{ width: 220 }}
            allowClear
            onChange={(e) => setFilters(f => ({ ...f, tenant: e.target.value }))}
          />
          <Select
            placeholder="Statut"
            style={{ width: 160 }}
            allowClear
            onChange={(val) => setFilters(f => ({ ...f, status: val || '' }))}
            options={Object.entries(STATUS_LABELS).map(([k, v]) => ({ value: k, label: v }))}
          />
        </Space>
      </Card>

      <Card style={{ borderRadius: 12, border: '1px solid #E5E7EB' }}>
        <Table
          columns={columns}
          dataSource={invoices}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 25, showSizeChanger: false }}
          size="middle"
        />
      </Card>
    </div>
  );
}

export default BillingInvoiceListPage;
